import type { FeatureCollection } from 'geojson'
import type * as maplibregl from 'maplibre-gl'
import type { ExpressionSpecification } from 'maplibre-gl'
import { getOddIconSvg, ODD_PNG_MAP } from './odd-icons'
import {
  type PMTilesLayerRef,
  pmtilesSourceUrl,
  registerPMTilesArchive,
} from './pmtiles'

// PNG icon configuration matching overwatch OddElementsLayer
const MARKER_ICON_PX = 60
const MARKER_PIXEL_RATIO = 2
// logical size = MARKER_ICON_PX / MARKER_PIXEL_RATIO = 30px
const MARKER_RADIUS = 15 // logical radius = 30 / 2

// Arrow dimensions (overwatch: ARROW_H=10, offset = -(MARKER_RADIUS + ARROW_H/2 + 2))
const ARROW_H = 10
const ARROW_OFFSET_Y = -(MARKER_RADIUS + ARROW_H / 2 + 2) // = -22

// Arrow image IDs
const ARROW_ORANGE_ID = 'odd-arrow-orange'
const ARROW_BLUE_ID = 'odd-arrow-blue'

const ARROW_W = 14

/** Draw an upward-pointing arrow (white fill, colored stroke) matching overwatch. */
function createArrowImageData(strokeColor: string): ImageData {
  const canvas = document.createElement('canvas')
  canvas.width = ARROW_W
  canvas.height = ARROW_H
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#FFFFFF'
  ctx.strokeStyle = strokeColor
  ctx.lineWidth = 1.5
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(ARROW_W / 2, 1) // tip
  ctx.lineTo(ARROW_W - 1, ARROW_H - 1) // bottom right
  ctx.lineTo(1, ARROW_H - 1) // bottom left
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  return ctx.getImageData(0, 0, ARROW_W, ARROW_H)
}

async function ensureArrowImages(map: maplibregl.Map) {
  if (!map.hasImage(ARROW_ORANGE_ID)) {
    const data = createArrowImageData('#EF6C00')
    map.addImage(ARROW_ORANGE_ID, data)
  }
  if (!map.hasImage(ARROW_BLUE_ID)) {
    const data = createArrowImageData('#2196F3')
    map.addImage(ARROW_BLUE_ID, data)
  }
}

// ─── Lane directional arrows ────────────────────────────────────────────────
// Ported from admt-web-app: an up-pointing arrow image placed along one-way
// lane lines via MapLibre's native `symbol-placement: 'line'`. Line placement
// aligns the image's +x axis with the travel direction, so the up-pointing
// SVG is pre-rotated 90° to point along the lane.

/**
 * Darken a hex color by a given factor (0–1) so directional arrows read a shade
 * deeper than the lane line they sit on — mirrors admt-web-app's separate,
 * darker `arrowColor`.
 */
const darkenColor = (hex: string, factor = 0.35): string => {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) return hex
  const channel = (start: number) => {
    const value = Number.parseInt(normalized.slice(start, start + 2), 16)
    return Math.round(value * (1 - factor))
      .toString(16)
      .padStart(2, '0')
  }
  return `#${channel(0)}${channel(2)}${channel(4)}`
}

const laneArrowImageId = (color: string) =>
  `lane-arrow-${color.replace('#', '').toLowerCase()}`

export const laneArrowSourceId = (id: string) => `${id}-arrows-source`

/** Up-pointing arrow SVG (matches admt-web-app's lane arrow glyph). */
const createLaneArrowSvg = (color: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg width="17" height="22" viewBox="0 0 17 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.3057 8.15287L14.3355 10.0552L9.51169 5.23143L9.51169 21.741L6.79406 21.741L6.79406 5.23143L1.97028 10.0552L-1.17119e-07 8.15288L8.15287 3.03708e-07L16.3057 8.15287Z" fill="${color}"/></svg>`,
  )}`

// ─── Geo helpers for evenly-spaced arrow placement ──────────────────────────
// Native `symbol-placement: 'line'` spaces symbols in screen pixels (so they
// jump on zoom) and restarts per MultiLineString segment (uneven gaps). admt
// avoids both by pre-computing arrow points at fixed *geographic* distances.

const EARTH_RADIUS_M = 6_371_000
const toRad = (deg: number) => (deg * Math.PI) / 180

/**
 * Great-circle distance in metres between two `[lng, lat]` pairs.
 *
 * Exported because callers outside this module measure geographic distance too
 * (the Route Builder's "notes near this route" filter), and a second copy would
 * be a second place for the earth radius and the argument order to drift.
 */
export const haversineMeters = (
  [lng1, lat1]: number[],
  [lng2, lat2]: number[],
): number => {
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

/**
 * Whether a coordinate pair can be handed to MapLibre at all.
 *
 * `new LngLat()` throws on a non-finite value or a latitude outside ±90, and a
 * marker built in a loop takes the whole map down with it — the throw escapes
 * through the effect that was drawing it, so React unmounts the view rather
 * than dropping one pin.
 *
 * Lives beside `haversineMeters` rather than with any one layer's code because
 * every producer of map coordinates needs the same guard: annotation rows from
 * recording devices, and coordinates typed into the Route Builder by hand.
 */
export const isRenderableCoordinate = (
  latitude: number,
  longitude: number,
): boolean =>
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  latitude >= -90 &&
  latitude <= 90 &&
  longitude >= -180 &&
  longitude <= 180

/** Bearing in degrees clockwise from north (0–360). */
const bearingDegrees = (
  [lng1, lat1]: number[],
  [lng2, lat2]: number[],
): number => {
  const dLng = toRad(lng2 - lng1)
  const y = Math.sin(dLng) * Math.cos(toRad(lat2))
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

const lineLengthMeters = (coords: number[][]): number => {
  let total = 0
  for (let i = 0; i < coords.length - 1; i++)
    total += haversineMeters(coords[i], coords[i + 1])
  return total
}

/** Interpolated point at a geographic distance (m) along a polyline. */
const pointAtDistance = (
  coords: number[][],
  targetDistance: number,
): [number, number] | null => {
  let accumulated = 0
  for (let i = 0; i < coords.length - 1; i++) {
    const segment = haversineMeters(coords[i], coords[i + 1])
    if (segment === 0) continue
    if (accumulated + segment >= targetDistance) {
      const fraction = (targetDistance - accumulated) / segment
      const [lng1, lat1] = coords[i]
      const [lng2, lat2] = coords[i + 1]
      return [lng1 + (lng2 - lng1) * fraction, lat1 + (lat2 - lat1) * fraction]
    }
    accumulated += segment
  }
  const last = coords[coords.length - 1]
  return last ? [last[0], last[1]] : null
}

/** Arrow spacing (m) by road type — mirrors admt's getBaseInterval. */
const arrowInterval = (roadType: unknown): number => {
  const type = typeof roadType === 'string' ? roadType.toLowerCase() : ''
  if (type.includes('roundabout')) return 15
  if (
    type.includes('highway') ||
    type.includes('motorway') ||
    type.includes('freeway')
  )
    return 50
  return 40
}

/**
 * Build a FeatureCollection of evenly-spaced arrow points (with a per-point
 * `bearing`) from one-way lane lines. Handles LineString and MultiLineString.
 */
export const buildLaneArrowPoints = (
  geojson: FeatureCollection,
): FeatureCollection => {
  const features: FeatureCollection['features'] = []
  const START_OFFSET = 10
  const END_OFFSET = 10

  for (const feature of geojson.features) {
    if (feature.properties?.oneway !== true) continue
    const geometry = feature.geometry
    if (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString')
      continue

    const lines =
      geometry.type === 'LineString'
        ? [geometry.coordinates as number[][]]
        : (geometry.coordinates as number[][][])

    const interval = arrowInterval(feature.properties?.road_type)

    for (const coords of lines) {
      if (coords.length < 2) continue
      const length = lineLengthMeters(coords)
      if (length < 20) continue

      const distances: number[] = []
      if (length > START_OFFSET) distances.push(START_OFFSET)
      let d = START_OFFSET + interval
      while (d < length - END_OFFSET) {
        distances.push(d)
        d += interval
      }

      for (const distance of distances) {
        const point = pointAtDistance(coords, distance)
        if (!point) continue
        const lookahead = Math.min(5, (length - distance) * 0.5)
        if (lookahead < 1) continue
        const next = pointAtDistance(coords, distance + lookahead)
        if (!next) continue
        if (
          Math.abs(point[0] - next[0]) < 1e-5 &&
          Math.abs(point[1] - next[1]) < 1e-5
        )
          continue

        features.push({
          type: 'Feature',
          properties: { bearing: bearingDegrees(point, next) },
          geometry: { type: 'Point', coordinates: point },
        })
      }
    }
  }

  return { type: 'FeatureCollection', features }
}

/** Load the arrow image for a given lane color into the map (idempotent). */
const ensureLaneArrowImage = (
  map: maplibregl.Map,
  color: string,
): Promise<string> => {
  const imageName = laneArrowImageId(color)
  if (map.hasImage(imageName)) return Promise.resolve(imageName)
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      if (!map.hasImage(imageName)) {
        map.addImage(imageName, img, { pixelRatio: 2 })
      }
      resolve(imageName)
    }
    img.onerror = reject
    img.src = createLaneArrowSvg(color)
  })
}

export const laneArrowLayerId = (id: string) => `${id}-arrows`

// ─── Hybrid lane rendering (GeoJSON low-zoom + PMTiles high-zoom) ────────────
// The PMTiles archive generalizes its road graph per zoom, so at low zoom it
// packs the whole metro into a coarse tile with only a fraction of the lanes
// present (verified: ~49km of in-geonet AV lane at z10 vs ~992km at z14). MVT
// can't recover that detail by overzooming downward, so a PMTiles-only lane
// looks empty until you zoom in. To render lanes fully at every zoom we layer
// them: PMTiles renders at ALL zooms but *below* the full, ungeneralized GeoJSON,
// which is drawn on top and capped at LANE_PMTILES_MIN_ZOOM. Below the threshold
// you see the complete GeoJSON; zooming past it hides that top layer and reveals
// the PMTiles that was already rendered beneath — so lanes just show/hide on
// zoom with no load flash at the handoff. Both are confined by the same geonet
// `within` clip.

/**
 * Zoom above which the GeoJSON lane layer hides, leaving only the PMTiles beneath
 * it. Archive maxzoom is 14–15, so from here PMTiles already has its full lane
 * detail (and overzooms above it); the hardcoded GeoJSON is only needed below,
 * where the archive is too generalized to look complete.
 */
export const LANE_PMTILES_MIN_ZOOM = 15
/** Source/layer ids for the low-zoom GeoJSON half of a hybrid lane. */
export const laneLowSourceId = (id: string) => `${id}-lo-source`
export const laneLowLineId = (id: string) => `${id}-lo-line`

// ─── Geonet clipping (within filter) ────────────────────────────────────────
// Lanes are confined to the combined area of the visible geonet polygons via a
// `within` expression (see ./geonet-clip). These helpers combine that clip with
// each lane layer's own base filter so the line, fill, and arrow layers all
// agree — both when a layer is first added and when the geonet set changes.

/** Feature filter for one-way lane segments (drives directional arrows). */
const ONEWAY_FILTER: ExpressionSpecification = ['==', ['get', 'oneway'], true]

/**
 * Combine a layer's base filter with the geonet `within` clip. Returns the base
 * alone when there is no clip, the clip alone when there is no base, and an
 * `['all', …]` of both otherwise.
 */
export const combineWithin = (
  base: ExpressionSpecification | undefined,
  within: ExpressionSpecification | null | undefined,
): ExpressionSpecification | undefined => {
  if (!within) return base
  if (!base) return within
  return ['all', base, within]
}

/** Base filter for a PMTiles lane's directional arrows (one-way ∧ ref.filter). */
const pmtilesArrowBaseFilter = (
  ref: PMTilesLayerRef,
): ExpressionSpecification =>
  ref.filter ? ['all', ONEWAY_FILTER, ref.filter] : ONEWAY_FILTER

/**
 * Re-apply the geonet `within` clip to an already-rendered lane's line and arrow
 * layers. Used when the visible geonet set changes: rather than rebuilding the
 * lane, we just update the filters in place.
 */
export function applyLaneGeonetClip(
  map: maplibregl.Map,
  id: string,
  ref: PMTilesLayerRef | undefined,
  within: ExpressionSpecification | null,
) {
  const arrowsId = laneArrowLayerId(id)
  if (map.getLayer(`${id}-line`)) {
    map.setFilter(`${id}-line`, combineWithin(ref?.filter, within) ?? null)
  }
  if (map.getLayer(arrowsId)) {
    // PMTiles arrows filter on the vector source (one-way ∧ ref.filter); GeoJSON
    // arrows are a precomputed point source with no base filter.
    const arrowBase = ref ? pmtilesArrowBaseFilter(ref) : undefined
    map.setFilter(arrowsId, combineWithin(arrowBase, within) ?? null)
  }
  // The low-zoom GeoJSON half (hybrid lanes) has no base filter — clip alone.
  const lowLineId = laneLowLineId(id)
  if (map.getLayer(lowLineId)) {
    map.setFilter(lowLineId, within ?? null)
  }
}

const GEONET_FILL_OPACITY = 0.07

/** Geonet fill opacity; none when the geonet is drawn as an outline only. */
export const geonetFillOpacity = (outlineOnly: boolean) =>
  outlineOnly ? 0 : GEONET_FILL_OPACITY

export function addFillLayer(
  map: maplibregl.Map,
  id: string,
  geojson: FeatureCollection,
  color: string,
  beforeId?: string,
  fillOpacity = GEONET_FILL_OPACITY,
) {
  const sourceId = `${id}-source`
  if (map.getSource(sourceId)) {
    map.removeLayer(`${id}-outline`)
    map.removeLayer(`${id}-fill`)
    map.removeSource(sourceId)
  }
  map.addSource(sourceId, { type: 'geojson', data: geojson })

  const layers = map.getStyle().layers
  // Fill goes below all line layers (lanes) — insert before the first line or symbol layer.
  const fillBefore =
    beforeId ?? layers.find(l => l.type === 'line' || l.type === 'symbol')?.id
  // Outline goes above lanes but below labels — insert before the first symbol layer.
  const outlineBefore = beforeId ?? layers.find(l => l.type === 'symbol')?.id

  map.addLayer(
    {
      id: `${id}-fill`,
      type: 'fill',
      source: sourceId,
      paint: { 'fill-color': color, 'fill-opacity': fillOpacity },
    },
    fillBefore,
  )
  map.addLayer(
    {
      id: `${id}-outline`,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.9 },
    },
    outlineBefore,
  )
}

export function addLineLayer(
  map: maplibregl.Map,
  id: string,
  geojson: FeatureCollection,
  color: string,
  beforeId?: string,
  geonetWithin?: ExpressionSpecification | null,
) {
  const sourceId = `${id}-source`
  const arrowsId = laneArrowLayerId(id)
  const arrowsSourceId = laneArrowSourceId(id)
  if (map.getSource(sourceId)) {
    if (map.getLayer(arrowsId)) map.removeLayer(arrowsId)
    if (map.getSource(arrowsSourceId)) map.removeSource(arrowsSourceId)
    map.removeLayer(`${id}-line`)
    map.removeSource(sourceId)
  }
  map.addSource(sourceId, { type: 'geojson', data: geojson })
  map.addLayer(
    {
      id: `${id}-line`,
      type: 'line',
      source: sourceId,
      // Confine lanes to the visible geonet zones (no base filter for GeoJSON).
      ...(geonetWithin ? { filter: geonetWithin } : {}),
      paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.7 },
    },
    beforeId,
  )

  // Directional arrows along one-way lanes. Rather than native
  // `symbol-placement: 'line'` (which spaces in screen pixels — arrows jump on
  // zoom — and restarts per MultiLineString segment — uneven gaps), we
  // pre-compute arrow points at fixed geographic distances with a per-point
  // `bearing` and render them as fixed point symbols. Matches admt-web-app.
  // The arrow uses a darker shade of the lane color so it reads on top of the
  // line. The image loads asynchronously; guard against teardown first.
  const arrowColor = darkenColor(color)
  const arrowData = buildLaneArrowPoints(geojson)
  void ensureLaneArrowImage(map, arrowColor).then(imageName => {
    // Bail if the layer was torn down, its arrows already exist, or the source
    // was swapped to a PMTiles vector source (a stale PMTiles→GeoJSON fallback
    // must not add GeoJSON arrows onto a vector source, or vice versa).
    if (map.getSource(sourceId)?.type !== 'geojson' || map.getLayer(arrowsId))
      return
    map.addSource(arrowsSourceId, { type: 'geojson', data: arrowData })
    map.addLayer(
      {
        id: arrowsId,
        type: 'symbol',
        source: arrowsSourceId,
        minzoom: 17,
        // Keep arrows inside the same geonet zones as their lane line.
        ...(geonetWithin ? { filter: geonetWithin } : {}),
        layout: {
          'icon-image': imageName,
          'icon-size': 0.8,
          // SVG arrow points up (north = bearing 0); rotate by the point's
          // geographic bearing so it points along the lane's travel direction.
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: { 'icon-opacity': 0.9 },
      },
      map.getLayer(`${id}-line`) ? undefined : beforeId,
    )
    // Keep arrows directly above their own lane line.
    if (map.getLayer(`${id}-line`)) map.moveLayer(arrowsId)
  })
}

// ─── PMTiles vector-tile variants ───────────────────────────────────────────
// These mirror addFillLayer / addLineLayer but read from a remote PMTiles vector
// source (JWT-authenticated, per-environment host — see ./pmtiles) instead of a
// static GeoJSON FeatureCollection. Layer/source ids match the GeoJSON variants
// (`${id}-source`, `${id}-fill`, `${id}-outline`, `${id}-line`, `${id}-arrows`)
// so removeMapLayer and the z-order logic in useMapOverlays work unchanged, and
// so a runtime fallback can swap one for the other under the same ids.

/** Add (or replace) the shared vector source for a PMTiles-backed layer. */
function ensureVectorSource(
  map: maplibregl.Map,
  sourceId: string,
  ref: PMTilesLayerRef,
) {
  if (map.getSource(sourceId)) return
  registerPMTilesArchive(ref.regionId)
  map.addSource(sourceId, {
    type: 'vector',
    url: pmtilesSourceUrl(ref.regionId),
  })
}

export function addVectorFillLayer(
  map: maplibregl.Map,
  id: string,
  ref: PMTilesLayerRef,
  color: string,
  beforeId?: string,
) {
  const sourceId = `${id}-source`
  if (map.getLayer(`${id}-outline`)) map.removeLayer(`${id}-outline`)
  if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`)
  ensureVectorSource(map, sourceId, ref)

  const layers = map.getStyle().layers
  const fillBefore =
    beforeId ?? layers.find(l => l.type === 'line' || l.type === 'symbol')?.id
  const outlineBefore = beforeId ?? layers.find(l => l.type === 'symbol')?.id

  map.addLayer(
    {
      id: `${id}-fill`,
      type: 'fill',
      source: sourceId,
      'source-layer': ref.sourceLayer,
      ...(ref.filter ? { filter: ref.filter } : {}),
      paint: { 'fill-color': color, 'fill-opacity': 0.07 },
    },
    fillBefore,
  )
  map.addLayer(
    {
      id: `${id}-outline`,
      type: 'line',
      source: sourceId,
      'source-layer': ref.sourceLayer,
      ...(ref.filter ? { filter: ref.filter } : {}),
      paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.9 },
    },
    outlineBefore,
  )
}

export function addVectorLineLayer(
  map: maplibregl.Map,
  id: string,
  ref: PMTilesLayerRef,
  color: string,
  beforeId?: string,
  geonetWithin?: ExpressionSpecification | null,
) {
  const sourceId = `${id}-source`
  const arrowsId = laneArrowLayerId(id)
  if (map.getLayer(arrowsId)) map.removeLayer(arrowsId)
  if (map.getLayer(`${id}-line`)) map.removeLayer(`${id}-line`)
  ensureVectorSource(map, sourceId, ref)

  const lineFilter = combineWithin(ref.filter, geonetWithin)
  // In hybrid mode PMTiles renders at ALL zooms but sits *below* the GeoJSON
  // half (see addGeoJsonLaneLowLayer + applyLayerZOrder). The GeoJSON on top is
  // capped at LANE_PMTILES_MIN_ZOOM, so zooming past it just hides that layer and
  // reveals the already-rendered PMTiles beneath — no load flash at the handoff.
  map.addLayer(
    {
      id: `${id}-line`,
      type: 'line',
      source: sourceId,
      'source-layer': ref.sourceLayer,
      ...(lineFilter ? { filter: lineFilter } : {}),
      paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.7 },
    },
    beforeId,
  )

  // Directional arrows along one-way lanes. Vector tiles don't give us full line
  // geometry up front, so — unlike the GeoJSON path's pre-computed geographic
  // points — we use MapLibre's native `symbol-placement: 'line'`. That aligns the
  // icon's +x axis with the travel direction; our arrow glyph points up (+y), so
  // it must be pre-rotated 90° to point along the lane. Matches admt-web-app's
  // PMTiles lane arrows.
  const arrowColor = darkenColor(color)
  const arrowFilter = combineWithin(pmtilesArrowBaseFilter(ref), geonetWithin)
  void ensureLaneArrowImage(map, arrowColor).then(imageName => {
    // Bail if torn down, arrows already exist, or the source was swapped to a
    // GeoJSON fallback source (a stale PMTiles arrow add must not land on it).
    if (map.getSource(sourceId)?.type !== 'vector' || map.getLayer(arrowsId))
      return
    map.addLayer(
      {
        id: arrowsId,
        type: 'symbol',
        source: sourceId,
        'source-layer': ref.sourceLayer,
        minzoom: 17,
        filter: arrowFilter,
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 150,
          'icon-image': imageName,
          'icon-size': 0.8,
          'icon-rotate': 90,
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: { 'icon-opacity': 0.9 },
      },
      map.getLayer(`${id}-line`) ? undefined : beforeId,
    )
    if (map.getLayer(`${id}-line`)) map.moveLayer(arrowsId)
  })
}

/**
 * Add the low-zoom half of a hybrid lane: the full, ungeneralized GeoJSON lanes,
 * rendered only below `maxzoom` where the PMTiles archive is too generalized to
 * look complete. Confined to the geonet zones by the same `within` clip as the
 * PMTiles half. No directional arrows — those are minzoom 17, well inside the
 * PMTiles range, so the low half never needs them.
 */
export function addGeoJsonLaneLowLayer(
  map: maplibregl.Map,
  id: string,
  geojson: FeatureCollection,
  color: string,
  beforeId?: string,
  geonetWithin?: ExpressionSpecification | null,
  maxzoom: number = LANE_PMTILES_MIN_ZOOM,
) {
  const sourceId = laneLowSourceId(id)
  const lineId = laneLowLineId(id)
  if (map.getLayer(lineId)) map.removeLayer(lineId)
  if (map.getSource(sourceId)) map.removeSource(sourceId)
  map.addSource(sourceId, { type: 'geojson', data: geojson })
  map.addLayer(
    {
      id: lineId,
      type: 'line',
      source: sourceId,
      maxzoom,
      ...(geonetWithin ? { filter: geonetWithin } : {}),
      paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.7 },
    },
    beforeId,
  )
}

export function removeMapLayer(
  map: maplibregl.Map,
  id: string,
  type: 'fill' | 'line',
) {
  const sourceId = `${id}-source`
  if (type === 'fill') {
    if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`)
    if (map.getLayer(`${id}-outline`)) map.removeLayer(`${id}-outline`)
  } else {
    const arrowsId = laneArrowLayerId(id)
    const arrowsSourceId = laneArrowSourceId(id)
    if (map.getLayer(arrowsId)) map.removeLayer(arrowsId)
    if (map.getSource(arrowsSourceId)) map.removeSource(arrowsSourceId)
    if (map.getLayer(`${id}-line`)) map.removeLayer(`${id}-line`)
    // Hybrid lanes also carry a low-zoom GeoJSON line + source.
    const lowLineId = laneLowLineId(id)
    const lowSourceId = laneLowSourceId(id)
    if (map.getLayer(lowLineId)) map.removeLayer(lowLineId)
    if (map.getSource(lowSourceId)) map.removeSource(lowSourceId)
  }
  if (map.getSource(sourceId)) map.removeSource(sourceId)
}

export function oddSourceId(type: string) {
  return `odd-${type}-source`
}

export function oddLayerId(type: string) {
  return `odd-${type}-layer`
}

export function oddArrowLayerId(type: string) {
  return `odd-${type}-arrow-layer`
}

export function oddImageId(type: string) {
  return `odd-icon-${type}`
}

const iconLoadingPromises = new Map<string, Promise<void>>()

const renderOddIconImageData = (type: string): Promise<ImageData> =>
  new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = MARKER_ICON_PX
      canvas.height = MARKER_ICON_PX
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, MARKER_ICON_PX, MARKER_ICON_PX)
      resolve(ctx.getImageData(0, 0, MARKER_ICON_PX, MARKER_ICON_PX))
    }
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(getOddIconSvg(type, MARKER_ICON_PX))}`
  })

export async function ensureOddIcon(
  map: maplibregl.Map,
  type: string,
): Promise<void> {
  const id = oddImageId(type)
  if (map.hasImage(id)) return

  const existing = iconLoadingPromises.get(id)
  if (existing) return existing

  const iconUrl = ODD_PNG_MAP[type]
  const promise: Promise<void> = iconUrl
    ? fetch(iconUrl)
        .then(res => res.blob())
        .then(blob => createImageBitmap(blob))
        .then(bitmap => {
          if (!map.hasImage(id)) {
            map.addImage(id, bitmap, { pixelRatio: MARKER_PIXEL_RATIO })
          }
        })
        .catch(() => renderOddIconImageData(type))
        .then(imageData => {
          if (imageData instanceof ImageData && !map.hasImage(id)) {
            map.addImage(id, imageData, { pixelRatio: MARKER_PIXEL_RATIO })
          }
        })
    : renderOddIconImageData(type).then(imageData => {
        if (!map.hasImage(id)) {
          map.addImage(id, imageData, { pixelRatio: MARKER_PIXEL_RATIO })
        }
      })

  const loadingPromise = promise
    .finally(() => {
      iconLoadingPromises.delete(id)
    })
    .then(() => undefined)

  iconLoadingPromises.set(id, loadingPromise)
  return loadingPromise
}

export function removeOddLayer(map: maplibregl.Map, type: string) {
  const arrowLid = oddArrowLayerId(type)
  const lid = oddLayerId(type)
  const sid = oddSourceId(type)
  if (map.getLayer(arrowLid)) map.removeLayer(arrowLid)
  if (map.getLayer(lid)) map.removeLayer(lid)
  if (map.getSource(sid)) map.removeSource(sid)
}

export async function addOddSymbolLayer(
  map: maplibregl.Map,
  type: string,
  geojson: FeatureCollection,
  iconOpacity = 1,
  beforeId?: string,
) {
  await ensureOddIcon(map, type)
  await ensureArrowImages(map)
  removeOddLayer(map, type)

  const sid = oddSourceId(type)
  const lid = oddLayerId(type)
  const arrowLid = oddArrowLayerId(type)

  map.addSource(sid, { type: 'geojson', data: geojson })

  // Heading arrow layer — rendered behind the icon layer
  // icon-rotate reads 'heading' property; only shown where heading exists
  map.addLayer(
    {
      id: arrowLid,
      type: 'symbol',
      source: sid,
      minzoom: 14,
      filter: ['has', 'heading'],
      layout: {
        'icon-image': [
          'case',
          ['boolean', ['get', 'routeGenerated'], false],
          ARROW_BLUE_ID,
          ARROW_ORANGE_ID,
        ],
        'icon-rotate': ['get', 'heading'],
        'icon-rotation-alignment': 'map',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.1, 15, 0.8],
        'icon-anchor': 'center',
        'icon-offset': [0, ARROW_OFFSET_Y],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        'icon-opacity': iconOpacity,
      },
    },
    beforeId,
  )

  // Main ODD icon layer — on top of arrows
  map.addLayer(
    {
      id: lid,
      type: 'symbol',
      source: sid,
      minzoom: 14,
      layout: {
        'icon-image': oddImageId(type),
        'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.1, 15, 0.8],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        // Full opacity for on-route elements (renderFullOpacity=true), dim others.
        // Default false so global iconOpacity applies when property is absent.
        'icon-opacity': [
          'case',
          ['boolean', ['get', 'renderFullOpacity'], false],
          1,
          iconOpacity,
        ],
      },
    },
    beforeId,
  )

  // Keep arrow layer behind icon layer
  if (map.getLayer(arrowLid) && map.getLayer(lid)) {
    map.moveLayer(arrowLid, lid)
  }
}

/** Update icon-opacity on all active ODD layers to reflect route presence */
export function setOddLayerOpacity(
  map: maplibregl.Map,
  type: string,
  opacity: number,
) {
  const lid = oddLayerId(type)
  const arrowLid = oddArrowLayerId(type)
  if (map.getLayer(lid)) {
    map.setPaintProperty(lid, 'icon-opacity', [
      'case',
      ['boolean', ['get', 'renderFullOpacity'], false],
      1,
      opacity,
    ])
  }
  if (map.getLayer(arrowLid)) {
    map.setPaintProperty(arrowLid, 'icon-opacity', opacity)
  }
}
